import React, { useEffect, useRef } from 'react';
import moment from 'moment';

export default function OverviewTab({
  deviceData = {},
  internalLimit = 60,
  complianceLimit = 80,
  unit = 'm³',
  maxExtraction = 0,
  avgExtraction = 0,
  reverseFlow = 0.0,
  breachesCount = 0,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onExport,
  onViewCert,
  onOpenMeterPhoto,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Initialize or Update Leaflet Map
  useEffect(() => {
    const lat = Number(deviceData.lat) || 28.541505;
    const lng = Number(deviceData.lng) || 77.332897;
    const label = `${deviceData.productId || 'Meter'}<br/>${deviceData.productName || 'Borewell'}`;

    if (!window.L || !mapRef.current) return;

    try {
      if (!mapInstanceRef.current) {
        if (mapRef.current._leaflet_id) {
          mapRef.current._leaflet_id = null;
        }

        const map = window.L.map(mapRef.current, { scrollWheelZoom: false }).setView([lat, lng], 13);
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '© OpenStreetMap contributors',
        }).addTo(map);

        const pinIcon = window.L.divIcon({
          className: '',
          iconSize: [30, 38],
          iconAnchor: [15, 36],
          popupAnchor: [0, -32],
          html: '<svg width="30" height="38" viewBox="0 0 30 38"><path d="M15 0C6.7 0 0 6.7 0 15c0 10 15 23 15 23s15-13 15-23C30 6.7 23.3 0 15 0z" fill="#1072bb"/><circle cx="15" cy="15" r="6" fill="#fff"/></svg>',
        });

        const marker = window.L.marker([lat, lng], { icon: pinIcon })
          .addTo(map)
          .bindPopup(`<b>${label}</b>`)
          .openPopup();

        mapInstanceRef.current = { map, marker };
      } else {
        // Smoothly pan map and update marker location when meter changes
        const { map, marker } = mapInstanceRef.current;
        map.setView([lat, lng], 13);
        if (marker) {
          marker.setLatLng([lat, lng]).setPopupContent(`<b>${label}</b>`).openPopup();
        }
      }
    } catch (err) {
      console.warn('Leaflet map error:', err);
    }
  }, [deviceData.lat, deviceData.lng, deviceData.productId, deviceData.productName]);

  // Clean up Leaflet on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.map.remove();
        } catch (e) {
          // ignore cleanup errors
        }
        mapInstanceRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current._leaflet_id = null;
      }
    };
  }, []);

  return (
    <div className="pane active">
      <div className="pane-tools">
        <h2>Overview</h2>
        <div className="range-ctl">
          <span className="rlab">From</span>
          <input
            type="date"
            className="rng-from"
            value={fromDate}
            onChange={(e) => onFromDateChange(e.target.value)}
          />
          <span className="rlab">To</span>
          <input
            type="date"
            className="rng-to"
            value={toDate}
            onChange={(e) => onToDateChange(e.target.value)}
          />
          <button className="btn xls" onClick={onExport}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
            </svg>
            Excel
          </button>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="stat-strip" style={{ marginBottom: '16px' }}>
        <div className="stat" style={{ '--tint': 'rgba(16,114,187,.08)' }}>
          <div className="s-ic">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18M12 3v18" />
            </svg>
          </div>
          <div className="s-lab">Max daily extraction</div>
          <div className="s-val">
            <span>{Number(maxExtraction).toFixed(2)}</span> <u>{unit}</u>
          </div>
        </div>

        <div className="stat" style={{ '--tint': 'rgba(14,164,114,.1)' }}>
          <div className="s-ic" style={{ background: 'var(--ok)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18M7 15l4-5 3 3 5-7" />
            </svg>
          </div>
          <div className="s-lab">Average daily extraction</div>
          <div className="s-val">
            <span>{Number(avgExtraction).toFixed(2)}</span> <u>{unit}</u>
          </div>
        </div>

        <div className="stat" style={{ '--tint': 'rgba(231,154,18,.12)' }}>
          <div className="s-ic" style={{ background: 'var(--warn)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 3l4 4-4 4M3 7h18M7 21l-4-4 4-4M21 17H3" />
            </svg>
          </div>
          <div className="s-lab">Reverse flow (month)</div>
          <div className="s-val">
            <span>{Number(reverseFlow).toFixed(2)}</span> <u>{unit}</u>
          </div>
        </div>

        <div className="stat" style={{ '--tint': 'rgba(229,72,77,.1)' }}>
          <div className="s-ic" style={{ background: 'var(--breach)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            </svg>
          </div>
          <div className="s-lab">Compliance breaches (30d)</div>
          <div className="s-val">
            <span>{breachesCount}</span> <u>days</u>
          </div>
        </div>
      </div>

      {/* Meter Details Card */}
      <div className="client-card">
        <div className="card-h">
          <h3>Meter details</h3>
        </div>

        <div className="mdetail">
          <div className="md">
            <div className="l">Product ID</div>
            <div className="v">{deviceData.productId || '—'}</div>
          </div>
          <div className="md">
            <div className="l">Serial number</div>
            <div className="v">{deviceData.flowmeterSerialNumber || '-'}</div>
          </div>
          <div className="md">
            <div className="l">Meter name</div>
            <div className="v">{deviceData.productName || 'Borewell'}</div>
          </div>
          <div className="md">
            <div className="l">Installation status</div>
            <div className="v">
              {deviceData.isRepairing ? (
                <span className="badge warn" style={{ background: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }}>Under repair</span>
              ) : deviceData.active === false ? (
                <span className="badge" style={{ background: '#f1f5f9', color: '#64748b', borderColor: '#cbd5e1' }}>Decommissioned</span>
              ) : (
                <span className="badge ok">Installed</span>
              )}
            </div>
          </div>
          <div className="md">
            <div className="l">Device model · DN size</div>
            <div className="v">
              {deviceData.model && deviceData.diameter
                ? `${deviceData.model} · DN${deviceData.diameter}`
                : deviceData.model
                ? deviceData.model
                : deviceData.diameter
                ? `DN${deviceData.diameter}`
                : '-'}
            </div>
          </div>
          <div className="md">
            <div className="l">Battery level</div>
            <div className="v">
              {deviceData.batteryLevel !== undefined && deviceData.batteryLevel !== null ? (
                <span style={{ fontWeight: 650, color: Number(deviceData.batteryLevel) <= 20 ? '#dc2626' : '#0d3b54', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle' }}>
                    <rect x="2" y="7" width="16" height="10" rx="2" ry="2" />
                    <line x1="22" y1="11" x2="22" y2="13" />
                  </svg>
                  {Number(deviceData.batteryLevel).toFixed(0)}%
                </span>
              ) : (
                <span style={{ color: '#94a3b8' }}>—</span>
              )}
            </div>
          </div>
          <div className="md">
            <div className="l">Internal limit</div>
            <div className="v">{Number(deviceData.internalLimit || internalLimit).toFixed(2)} {unit}</div>
          </div>
          <div className="md">
            <div className="l">Compliance limit</div>
            <div className="v">{Number(deviceData.complianceLimit || complianceLimit).toFixed(2)} {unit}</div>
          </div>
          <div className="md">
            <div className="l">Communication</div>
            <div className="v">
              <span className="badge live">Live</span>
            </div>
          </div>
          <div className="md">
            <div className="l">Last communicated</div>
            <div className="v">{deviceData.lastCommunicated || 'Recently'}</div>
          </div>
          <div className="md">
            <div className="l">Data plan</div>
            <div className="v">
              <span className="badge blue">Active</span>
            </div>
          </div>
          <div className="md">
            <div className="l">Data plan valid to</div>
            <div className="v">
              {deviceData.dashboardLicensingExpiry
                ? new Date(deviceData.dashboardLicensingExpiry).toLocaleDateString()
                : 'Active'}
            </div>
          </div>
          <div className="md">
            <div className="l">Warranty valid to</div>
            <div className="v">
              {deviceData.warrantyExpiry
                ? new Date(deviceData.warrantyExpiry).toLocaleDateString()
                : 'Standard warranty'}
            </div>
          </div>
          <div className="md">
            <div className="l">Calibration certificate</div>
            <div className="v">
              {deviceData.calibrationCertificate?.storedFileName || deviceData.calibrationCertificate?.fileName ? (
                <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#0d3b5e' }}>
                  {deviceData.calibrationCertificate?.expiryDate
                    ? `Valid to ${moment(deviceData.calibrationCertificate.expiryDate).format('DD/MM/YYYY')}`
                    : 'Uploaded'}
                </span>
              ) : (
                <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                  Not uploaded
                </span>
              )}
            </div>
          </div>
          <div className="md">
            <div className="l">Meter photo</div>
            <div className="v">
              {deviceData.meterPhoto?.storedFileName || deviceData.meterPhoto?.fileName ? (
                <span style={{ fontSize: '13.5px', fontWeight: 650, color: '#047857', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', display: 'inline-block' }}></span>
                  Uploaded ·{' '}
                  <a
                    href="#view-photo"
                    onClick={(e) => {
                      e.preventDefault();
                      onOpenMeterPhoto && onOpenMeterPhoto();
                    }}
                    style={{ color: 'var(--primary-dk)', fontWeight: 600, textDecoration: 'underline' }}
                  >
                    View / Edit
                  </a>
                </span>
              ) : (
                <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                  Not uploaded ·{' '}
                  <a
                    href="#upload-photo"
                    onClick={(e) => {
                      e.preventDefault();
                      onOpenMeterPhoto && onOpenMeterPhoto();
                    }}
                    style={{ color: 'var(--primary-dk)', fontWeight: 600 }}
                  >
                    Upload now
                  </a>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Meter Location Card */}
      <div className="client-card" style={{ marginTop: '16px' }}>
        <div className="card-h" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h3>Meter location</h3>
            <span className="sub">
              OpenStreetMap · GPS: {Number(deviceData.latitude || deviceData.lat || 28.541505).toFixed(6)}, {Number(deviceData.longitude || deviceData.lng || 77.332897).toFixed(6)}
            </span>
          </div>
          <button
            type="button"
            className="btn ghost"
            style={{
              fontSize: '12px',
              padding: '5px 12px',
              color: deviceData.meterPhoto?.fileName || deviceData.meterPhoto?.storedFileName ? '#047857' : undefined,
              borderColor: deviceData.meterPhoto?.fileName || deviceData.meterPhoto?.storedFileName ? '#a7f3d0' : undefined,
              background: deviceData.meterPhoto?.fileName || deviceData.meterPhoto?.storedFileName ? '#ecfdf5' : undefined,
            }}
            onClick={() => onOpenMeterPhoto && onOpenMeterPhoto()}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            <span>{deviceData.meterPhoto?.fileName || deviceData.meterPhoto?.storedFileName ? 'View / Change Photo' : 'Upload Meter Photo'}</span>
          </button>
        </div>
        <div id="client-map" ref={mapRef}></div>
      </div>
    </div>
  );
}
